import { Link, useHistory, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SubscriptionHelper } from 'view/helpers';

import { usePageTitle } from 'utils/pageTitle';
import {
	signIn,
	clearAuthErrors,
	resetJustSignedOut,
	signOut,
} from 'reducers/userReducer';

import Alert from 'components/Alert';
import Spinner from 'components/Spinner';

import './AuthPage.css';
import { AuthMapBackground } from 'view/marketing/components/site/auth-map-background';
import { getApiUrl } from 'data/services/apiConstants';
import { SocialLogins } from 'view/components/SocialLogin/SocialLogins';

const SignIn = (props) => {
	usePageTitle('Sign In');
	const dispatch = useDispatch();
	const history = useHistory();
	const location = useLocation();
	const { isSigningIn, signInErrors, justSignedOut, isLoggedIn } = useSelector(
		(state) => ({
			isSigningIn: state.user.isSigningIn,
			signInErrors: state.user.signInErrors,
			justSignedOut: state.user.justSignedOut,
			isLoggedIn: !!state.user.userData?.id,
		}),
	);
	const [emailAddress, setEmailAddress] = useState('');
	const [password, setPassword] = useState('');

	const [error, setError] = useState('');
	const [oauthProcessing, setOauthProcessing] = useState(false);

	/**
	 * Check if running in popup window
	 */
	const isPopup = () => {
		return window.opener && window.opener !== window;
	};

	/**
	 * Handle OAuth callback from URL parameters
	 * RUNS IMMEDIATELY when page loads with token/error params
	 */
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const token = searchParams.get('token');
		const provider = searchParams.get('provider');
		const errorParam = searchParams.get('error');

		// Check if we're in a popup window
		const inPopup = window.opener && window.opener !== window;

		console.log('OAuth Callback Detection:', {
			inPopup,
			hasToken: !!token,
			hasError: !!errorParam,
			provider,
			url: window.location.href,
		});

		// Handle OAuth error
		if (errorParam) {
			const decodedError = decodeURIComponent(errorParam);

			if (inPopup) {
				console.log('Popup: Storing error and closing');
				localStorage.setItem('oauth_error', decodedError);
				setTimeout(() => window.close(), 100);
			} else {
				setError(decodedError);
			}
			return;
		}

		// Handle OAuth success
		if (token && provider) {
			if (inPopup) {
				console.log('Popup: Storing token and closing', {
					provider,
					token: token.substring(0, 20) + '...',
				});

				// Store token in localStorage
				localStorage.setItem(`oauth_${provider}_token`, token);

				// Also try postMessage as backup
				try {
					window.opener.postMessage(
						{ type: 'OAUTH_SUCCESS', token, provider },
						window.location.origin,
					);
				} catch (e) {
					console.warn('postMessage failed:', e);
				}

				// Close popup immediately
				setTimeout(() => {
					console.log('Closing popup now');
					window.close();
				}, 100);
			} else {
				// Not in popup - regular page load with OAuth params
				console.log('Regular page: Handling OAuth directly');
				setOauthProcessing(true);
				handleOAuthSuccess(token, provider);
			}
		}
		// Handle the OAuth redirect once per query string; handleOAuthSuccess is
		// recreated each render, so listing it would re-run the sign-in.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.search]);

	/**
	 * Handle successful OAuth authentication
	 *
	 * @param {string} token - JWT token
	 * @param {string} provider - 'google' or 'microsoft'
	 */
	const handleOAuthSuccess = async (token, provider) => {
		try {
			// Store token
			localStorage.setItem('token', token);

			// Fetch user data
			const response = await fetch(`${getApiUrl()}/api/auth/user`, {
				headers: {
					Authorization: `Bearer ${token}`,
				},
			});

			const data = await response.json();

			if (data.status === 'success' && data.data) {
				// Dispatch to Redux
				dispatch({
					type: 'LOGIN_SUCCESS',
					payload: {
						user: data.data,
						token: token,
					},
				});

				if (isPopup()) {
					// Send success to parent window
					window.opener.postMessage(
						{ type: 'OAUTH_SUCCESS', token: token },
						window.location.origin,
					);

					// Show success message briefly before closing
					setOauthProcessing(false);
					setTimeout(() => window.close(), 1000);
				} else {
					// Regular redirect to dashboard
					history.push('/dashboard');
				}
			} else {
				throw new Error('Failed to fetch user data');
			}
		} catch (err) {
			console.error('OAuth success handler error:', err);
			const errorMessage = `Failed to complete ${provider} authentication`;

			if (isPopup()) {
				window.opener.postMessage(
					{ type: 'OAUTH_ERROR', error: errorMessage },
					window.location.origin,
				);
				setTimeout(() => window.close(), 2000);
			} else {
				setError(errorMessage);
				setOauthProcessing(false);
			}
		}
	};

	/**
	 * Sends the signed-in visitor on to the post-auth destination. A return to
	 * an invitation REPLACES this sign-in entry: left in history, Back from
	 * the dashboard after the join lands here, and this page, now signed in,
	 * pushes the visitor straight back onto the consumed invitation.
	 */
	const navigateAfterSignIn = (urlParams) => {
		const redirectUrl = SubscriptionHelper.getPostAuthRedirect(urlParams);

		if (urlParams.has(SubscriptionHelper.INVITATION_TOKEN_PARAM)) {
			history.replace(redirectUrl);
			return;
		}

		history.push(redirectUrl);
	};

	const onSubmit = () => {
		dispatch(resetJustSignedOut());
		return dispatch(signIn(emailAddress, password)).then((res) => {
			if (res?.payload?.userData?.id) {
				//sign in success
				// Check if user came from checkout flow using helper
				const urlParams = new URLSearchParams(location.search);
				navigateAfterSignIn(urlParams);
			}
		});
	};
	const onEnter = (event) => {
		if (event.key === 'Enter') {
			onSubmit();
		}
	};

	/* eslint-disable react-hooks/exhaustive-deps */
	useEffect(() => dispatch(clearAuthErrors()), []);

	useEffect(() => {
		if (isLoggedIn) {
			const urlParams = new URLSearchParams(location.search);
			navigateAfterSignIn(urlParams);
		}
	}, [isLoggedIn, history, location.search]);

	let alreadySignedIn = (
		<div className="sign-in-wrapper">
			<div className="alert alert-success">You're already signed in!</div>
			<div className="input-wrapper">
				<Link to="/dashboard/overview">View your dashboard</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/property">Find a property</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/use-policy">Acceptable Use Policy</Link>
			</div>
			<div className="input-wrapper">
				<button
					onClick={() => dispatch(signOut())}
					className="btn btn-secondary"
				>
					Sign Out
				</button>
			</div>
		</div>
	);

	let signInForm = (
		<div className="sign-in-wrapper">
			{isSigningIn && <Spinner />}
			{signInErrors.map((message) => (
				<Alert color="danger" text={message} key={message} />
			))}
			{justSignedOut && (
				<div className="alert alert-success">Signed out successfully!</div>
			)}

			<div className="input-wrapper">
				<label className="form-label">Email</label>
				<input
					type="text"
					value={emailAddress}
					onChange={(ev) => setEmailAddress(ev.target.value)}
					className="form-control"
					placeholder="Email"
					disabled={isSigningIn}
					onKeyDown={onEnter}
					autoFocus
				/>
			</div>

			<div className="input-wrapper">
				<label className="form-label">Password</label>
				<input
					type="password"
					value={password}
					onChange={(ev) => setPassword(ev.target.value)}
					className="form-control"
					placeholder="Password"
					disabled={isSigningIn}
					onKeyDown={onEnter}
				/>
			</div>

			<div className="input-wrapper">
				<button
					onClick={onSubmit}
					disabled={isSigningIn}
					className="btn btn-primary"
				>
					Sign In
				</button>
			</div>

			<div className="input-wrapper">
				<Link to="/forgot-password">Forgot your password?</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/sign-up">Don't have an account? Sign up!</Link>
			</div>
			<div className="input-wrapper">
				<Link to="/use-policy">Acceptable Use Policy</Link>
			</div>

			<SocialLogins title="Continue" />
		</div>
	);

	if (oauthProcessing && isPopup()) {
		return (
			<div className="oauth-popup-processing">
				<div className="popup-spinner"></div>
				<h2>Completing Sign In</h2>
				<p>Please wait...</p>

				<style jsx>{`
					.oauth-popup-processing {
						display: flex;
						flex-direction: column;
						align-items: center;
						justify-content: center;
						min-height: 100vh;
						background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
						color: white;
						font-family:
							-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
					}

					.popup-spinner {
						width: 48px;
						height: 48px;
						border: 4px solid rgba(255, 255, 255, 0.3);
						border-top: 4px solid white;
						border-radius: 50%;
						margin-bottom: 20px;
						animation: spin 1s linear infinite;
					}

					.oauth-popup-processing h2 {
						margin: 0 0 8px 0;
						font-size: 24px;
					}

					.oauth-popup-processing p {
						margin: 0;
						opacity: 0.9;
						font-size: 14px;
					}

					@keyframes spin {
						0% {
							transform: rotate(0deg);
						}
						100% {
							transform: rotate(360deg);
						}
					}
				`}</style>
			</div>
		);
	}

	return (
		<div className="auth-page" data-map-section>
			<AuthMapBackground />
			<div className="auth-page-content" data-map-protected>
				<h1>Sign In</h1>
				{/* Error Display */}
				{error && (
					<div className="auth-error">
						<span className="error-icon">⚠️</span>
						{error}
					</div>
				)}
				{isLoggedIn ? alreadySignedIn : signInForm}
			</div>
		</div>
	);
};

export default SignIn;
